import Image from "next/image"
import { type ReactElement } from "react"
import { Button, Container, Stack, Typography } from "@octopus-energy/coral/atoms"
import { QueryClient } from "@tanstack/react-query"
import { setCookie } from "cookies-next"
import { type GetServerSidePropsContext, type InferGetServerSidePropsType } from "next"
import { useTranslations } from "next-intl"
import { z } from "zod"
import CustomHead from "@/components/atoms/CustomHead"
import { Page } from "@/components/organisms"
import { AppLayout } from "@/features/navigation"
import {
    getPartnerConfig,
    Partner,
    PartnerPlugin,
    PartnerSessionManager,
} from "@/features/partners"
import { OEBackend } from "@/graphql"
import { oeAuth } from "@/lib/blueprint-auth/server"
import { apiRoutesPaths } from "@/routes/api"
import { portalRoutesPaths } from "@/routes/portail"
import { resolveDehydratedState, resolveMessages } from "@/utils/pageResolvers/server"

type AffiliatePortalPartnerLoginPageProps = InferGetServerSidePropsType<typeof getServerSideProps>

export default function AffiliatePortalPartnerLoginPage({
    organizationSsoUrl,
}: AffiliatePortalPartnerLoginPageProps) {
    const t = useTranslations("AffiliatePortalPartnerLoginPage")

    return (
        <Page>
            <CustomHead robots="noindex,nofollow" />
            <Container
                theme="none"
                paddingX={{ base: "md", sm: "lg" }}
            >
                <Stack
                    flexDirection="column"
                    alignItems="center"
                    gap="xxl"
                >
                    <Typography
                        textStyle="h3"
                        component="h1"
                        textAlign="center"
                    >
                        {t("title")}
                    </Typography>

                    <Image
                        src="/assets/images/illustrations/constantine-with-energy-stuff.svg"
                        alt={t("imageAlt")}
                        width={248}
                        height={192}
                    />

                    <Container width={{ base: "auto", sm: "1/2" }}>
                        <Button
                            fullWidth
                            href={organizationSsoUrl as string}
                        >
                            {t("login")}
                        </Button>
                    </Container>
                </Stack>
            </Container>
        </Page>
    )
}

AffiliatePortalPartnerLoginPage.getLayout = (
    page: ReactElement<AffiliatePortalPartnerLoginPageProps>
) => (
    <AppLayout
        isNavless
        partner={page.props.partnerSlug}
    >
        {page}
    </AppLayout>
)

AffiliatePortalPartnerLoginPage.messages = [
    "AffiliatePortalPartnerLoginPage",
    ...AppLayout.messages,
    ...PartnerPlugin.messages,
] as const

const AffiliatePortalPartnerLoginPageQuery = OEBackend.graphql(`
    query AffiliatePortalPartnerLoginPageQuery(
        $identityProviderSlug: NonEmptyString!
        $redirectUri: NonEmptyString!
        $state: NonEmptyString!
    ) {
        organizationSsoUrl(identityProviderSlug: $identityProviderSlug, redirectUri: $redirectUri, state: $state) 
    }
`)

const QueryParamsSchema = z.object({
    partnerSlug: z.nativeEnum(Partner),
})

export async function getServerSideProps(context: GetServerSidePropsContext) {
    const queryClient = new QueryClient()
    const oeOrgAuth = await oeAuth.getAuth.org({ context })

    if (!oeOrgAuth) {
        throw new Error("Failed to initialize GraphQL client.")
    }

    const oeOrgGraphQLClient = oeAuth.getGraphQLClient.org({ auth: oeOrgAuth })

    try {
        const parsedQueryParams = QueryParamsSchema.safeParse(context.query)

        if (!parsedQueryParams.success) {
            return { notFound: true }
        }

        const { partnerSlug } = parsedQueryParams.data

        const partnerConfig = getPartnerConfig(partnerSlug)
        const sessionManager = new PartnerSessionManager(context.req, context.res)
        const session = await sessionManager.getSession(partnerSlug)

        if (!partnerConfig.session || session) {
            // If the partner doesn't requires a session or if the user already has a session, redirect to the partner page
            return {
                redirect: {
                    destination: portalRoutesPaths.partnerPage.resolve({
                        pathParams: { partnerSlug },
                    }),
                },
            }
        }

        // Generate state for CSRF protection
        const state = crypto.randomUUID()
        const stateKey = `sso_state_${partnerSlug}`

        // Store state in HTTP-only cookie for security
        await setCookie(stateKey, state, {
            req: context.req,
            res: context.res,
            httpOnly: true,
            sameSite: "lax",
            path: "/",
            maxAge: 600, // 10 minutes
        })

        const callbackPath = apiRoutesPaths.partnerAuthCallback.resolve({
            pathParams: { partnerSlug },
        })
        const protocol = context.req.headers["x-forwarded-proto"] || "https"
        const host = context.req.headers.host

        const redirectUri = `${protocol}://${host}${callbackPath}`

        const { organizationSsoUrl } = await queryClient.fetchQuery({
            queryKey: ["AffiliatePortalPartnerLoginPageQuery"],
            queryFn() {
                return oeOrgGraphQLClient.request(AffiliatePortalPartnerLoginPageQuery, {
                    identityProviderSlug: partnerSlug,
                    redirectUri,
                    state,
                })
            },
        })

        const [dehydratedState, messages] = await Promise.all([
            resolveDehydratedState({ queryClient, context }),
            resolveMessages({
                context,
                intlNamespaces: AffiliatePortalPartnerLoginPage.messages,
            }),
        ])

        return {
            props: {
                partnerSlug,
                organizationSsoUrl,
                dehydratedState,
                messages,
                state,
            },
        }
    } catch {
        return { notFound: true }
    }
}
